import { Ticket as TicketIcon } from "lucide-react";

/**
 * OrthoTicket — pretty stylized voucher card (cinema-ticket inspired).
 *
 * Props:
 *  - pts:  number   (orthopoints cost: 20, 100, 200)
 *  - eur:  number   (face value: 1, 5, 10)
 *  - tone: "sage" | "pedablue" | "coral"
 *  - large / small: size variants
 *  - selected: highlight active border
 *  - disabled: dim non-claimable
 *  - onClick: action
 *  - count: badge "×3" on top right
 */
const TONES = {
  sage:     { bg: "bg-gradient-to-br from-sage/40 via-sage/20 to-sage/10",      stamp: "bg-sage text-white",      ring: "ring-sage/30",      text: "text-sage-dark",     accent: "text-sage" },
  pedablue: { bg: "bg-gradient-to-br from-pedablue/35 via-pedablue/15 to-pedablue/5", stamp: "bg-pedablue text-white", ring: "ring-pedablue/30",  text: "text-pedablue",       accent: "text-pedablue" },
  coral:    { bg: "bg-gradient-to-br from-coral/40 via-coral/20 to-coral/10",  stamp: "bg-coral-dark text-white", ring: "ring-coral/30",     text: "text-coral-dark",     accent: "text-coral-dark" },
};

export default function OrthoTicket({ pts, eur, tone = "coral", large, small, selected, disabled, count, onClick }) {
  const t = TONES[tone] || TONES.coral;
  const isInteractive = !!onClick;
  const Comp = isInteractive ? "button" : "div";
  const sizeCls = large
    ? "min-w-[260px] max-w-[320px] py-8 px-7"
    : small
      ? "max-w-[160px] py-4 px-4"
      : "max-w-[220px] py-5 px-5";

  return (
    <Comp
      onClick={isInteractive ? onClick : undefined}
      disabled={disabled}
      data-testid={`orthoticket-${pts}`}
      className={`relative ${t.bg} rounded-2xl ${sizeCls} text-center
        border-2 ${selected ? `border-${tone}` : "border-transparent"}
        ${disabled ? "opacity-40 cursor-not-allowed grayscale" : isInteractive ? `cursor-pointer hover:-translate-y-0.5 hover:shadow-lg ring-1 ${t.ring}` : "ring-1 " + t.ring}
        transition-all
        before:content-[''] before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:-translate-x-1/2 before:w-5 before:h-5 before:bg-ivory before:rounded-full
        after:content-[''] after:absolute after:right-0 after:top-1/2 after:-translate-y-1/2 after:translate-x-1/2 after:w-5 after:h-5 after:bg-ivory after:rounded-full
      `}
    >
      {/* Dashed center divider (decorative perforation) */}
      <div className="absolute left-6 right-6 top-1/2 border-t border-dashed border-current opacity-20 pointer-events-none" />

      <div className="relative flex items-center justify-center gap-2 mb-1">
        <TicketIcon className={t.accent} size={large ? 18 : 14} />
        <span className={`text-[10px] uppercase tracking-[0.2em] font-medium ${t.accent}`}>Orthoticket</span>
      </div>

      <p className={`font-display font-light leading-none ${t.text} ${large ? "text-6xl" : small ? "text-3xl" : "text-4xl"} mb-2`}>
        {eur}<span className="text-lg font-sans align-top ml-0.5">€</span>
      </p>
      <div className={`inline-block ${t.stamp} text-[10px] uppercase tracking-widest rounded-full px-3 py-1 mt-1`}>
        {pts} orthopoints
      </div>

      {count > 0 && (
        <span
          data-testid={`orthoticket-count-${pts}`}
          className="absolute -top-2 -right-2 bg-ink text-ivory rounded-full text-xs font-semibold w-8 h-8 flex items-center justify-center shadow-md ring-2 ring-ivory"
        >
          ×{count}
        </span>
      )}
    </Comp>
  );
}
